import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { HiddenInboxItem, InboxItem } from '../api/types.js';
import { useHiddenInbox, useInbox, useMoveToSubject, useSetAside } from '../hooks/use-inbox.js';
import { timeAgo } from '../utils/time-ago.js';
import { hiddenLine } from '../utils/set-aside.js';
import { SetAsideForm } from './set-aside-form.js';
import { useStartReview } from '../hooks/use-reviews.js';
import { useStartTicket } from '../hooks/use-tickets.js';
import { WAITING, waitingOf, type WaitingKind } from '../utils/waiting.js';

// « Waiting on you »: the decisions this server holds for the operator, in the order to take them (#760). The same list
// is To do, the home page, across servers (ADR-039 step 5): each line then says its server and acts on it.
// The API ranks them and says why; each line opens the place where the decision is taken. From the line, a decision
// can be set aside with what is missing (ADR-039 section 2), or marked done when it was handled elsewhere; both come
// back if the item changes.

const ACTION: Record<InboxItem['kind'], (title: string) => string> = {
  deployment: (t) => `Approve or reject ${t}`,
  analysis: (t) => `Approve or reject ${t}`,
  held: (t) => `Run or drop what is held on ${t}`,
  blocked: (t) => `${t}: relaunch it once the budget is raised`,
  waiting_for_input: (t) => `Answer the session ${t}`,
  replies: (t) => `Check the draft replies on ${t}`,
  review: (t) => `Submit the draft review of ${t}`,
  google: (t) => `Reconnect Google (${t})`,
  cost: (t) => `Look at this month's costs: ${t}`,
  calendar: (t) => `Approve or reject: ${t}`,
  document: (t) => `Approve or reject: ${t}`,
  improvement: (t) => `Open as an issue or dismiss: ${t}`,
  message: (t) => `Send or reject: ${t}`,
  ticket: (t) => `Decide on the plan for ${t}`,
  epic: (t) => `See where the epic ${t} stands`,
  ci: (t) => `${t}`,
  session: (t) => `Follow it: ${t}`,
  question: (t) => `Answer the session ${t}`,
  credential: (t) => `Renew the credential: ${t}`,
  mail: (t) => `Read and answer: ${t}`,
  jira: (t) => `Decide on the analysis of ${t}`,
  request: (t) => `Answer the request ${t}`,
  meeting: (t) => `Decide on the plan of the meeting ${t}`,
  action: (t) => `Approve or reject: ${t}`,
  slack: (t) => `Answer on Slack: ${t}`,
  gitlab: (t) => `Answer in GitLab: ${t}`,
};

/** Urgency on the left edge: blocking and risky first, then decisions, then drafts */
function edge(score: number): string {
  if (score >= 110) return 'var(--color-error)';
  if (score >= 70) return 'var(--color-warn)';
  return 'var(--color-border-2)';
}

function sentence(reasons: string[]): string {
  const text = reasons.join(', ');
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}.` : '';
}

/** The row an item points at, once the page has opened it */
function anchorOf(item: InboxItem): string | null {
  const q = new URLSearchParams(item.url.split('?')[1] ?? '');
  if (q.get('deploy')) return `deploy-row-${q.get('deploy')}`;
  if (q.get('review')) return `review-row-${q.get('review')}`;
  if (item.url.endsWith('#cost-title')) return 'cost-title';
  if (q.get('calendar')) return `calendar-${q.get('calendar')}`;
  if (q.get('document')) return `document-${q.get('document')}`;
  if (q.get('improvement')) return `improvement-${q.get('improvement')}`;
  if (q.get('message')) return `message-${q.get('message')}`;
  if (q.get('ticket')) return `ticket-${q.get('ticket')}`;
  if (q.get('analysis')) return `analysis-${q.get('analysis')}`;
  if (q.get('meeting')) return `meeting-${q.get('meeting')}`;
  if (q.get('action')) return `action-${q.get('action')}`;
  return null;
}

const quietButton = 'rounded px-1.5 py-0.5 text-xs hover:underline focus-visible:outline focus-visible:outline-2';

/**
 * The lines of a subject together (ADR-039 step 4a), at the rank of its best one: the list is ranked already, so a
 * subject's first line comes first and the others follow it. A subject with one line of the list is just that line.
 */
export function bySubject(list: InboxItem[]): InboxItem[] {
  // Counted on what the list shows: a sprint filter may leave one line of a subject
  const sizes = new Map<string, number>();
  for (const i of list) if (i.subject) sizes.set(i.subject.id, (sizes.get(i.subject.id) ?? 0) + 1);
  const items = list.map((i) => (i.subject ? { ...i, subject: { ...i.subject, size: sizes.get(i.subject.id)! } } : i));
  const out: InboxItem[] = [];
  const placed = new Set<string>();
  for (const item of items) {
    if (placed.has(item.id)) continue;
    const together = item.subject && item.subject.size > 1 ? items.filter((i) => i.subject?.id === item.subject!.id) : [item];
    for (const i of together) {
      out.push(i);
      placed.add(i.id);
    }
  }
  return out;
}

/**
 * Corrects the subject aidev gave the line (ADR-039 step 4c): out of its group into one of its own, or in with another
 * subject of the list. Kept, and the small model learns from it
 */
function SubjectFix({ item, serverId, subjects, onDone }: { item: InboxItem; serverId: string; subjects: Array<{ id: string; name: string; serverId: string }>; onDone: () => void }) {
  const move = useMoveToSubject(serverId);
  // A subject is of one server: an item moves among its own server's subjects only
  const others = subjects.filter((s) => s.serverId === serverId && s.id !== item.subject?.id);
  const grouped = !!item.subject && item.subject.size > 1;
  if (!grouped && !others.length) return null;
  const to = (target: string) => move.mutate({ itemId: item.id, to: target }, { onSuccess: onDone });
  return (
    <div role="group" aria-label="Correct the subject" className="flex flex-wrap items-baseline gap-x-2 gap-y-1.5 text-xs" style={{ color: 'var(--color-text-3)' }}>
      Wrong subject?
      {grouped && (
        <button type="button" disabled={move.isPending} className={quietButton} onClick={() => to('own')}
          style={{ color: 'var(--color-text)', border: '1px solid var(--color-border-2)' }}>
          Not about {item.subject!.name}
        </button>
      )}
      {others.length > 0 && (
        <label className="flex items-baseline gap-1.5">
          {grouped ? 'or same subject as' : 'Same subject as'}
          <select value="" disabled={move.isPending} onChange={(e) => e.target.value && to(e.target.value)}
            className="max-w-[14rem] truncate rounded px-1 py-0.5 text-xs focus-visible:outline focus-visible:outline-2"
            style={{ color: 'var(--color-text)', background: 'var(--color-surface)', border: '1px solid var(--color-border-2)' }}>
            <option value="">choose…</option>
            {others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      )}
      {move.isError && <span role="alert" className="block w-full" style={{ color: 'var(--color-error)' }}>Not moved: {(move.error as Error).message}</span>}
    </div>
  );
}

function Row({ item, rank, serverId: listServer, opens, inSubject, subjects = [] }: {
  item: InboxItem; rank: number; serverId: string; opens?: string; inSubject?: boolean; subjects?: Array<{ id: string; name: string; serverId: string }>;
}) {
  const [open, setOpen] = useState(false);
  const serverId = item.server?.id ?? listServer;
  const setAside = useSetAside(serverId);
  // A GitLab merge request asked of the operator (ADR-041 step 2): its review prepared on his click, never before; an
  // issue (step 3a): its plan, the same way
  const mergeRequest = item.kind === 'gitlab' && /^gitlab:.+!\d+$/.test(item.id) ? item.id.slice('gitlab:'.length) : null;
  const gitlabIssue = item.kind === 'gitlab' && /^gitlab:.+#\d+$/.test(item.id) ? item.id.slice('gitlab:'.length) : null;
  const review = useStartReview(serverId);
  const plan = useStartTicket(serverId);
  const prepare = gitlabIssue ? plan : review;
  const target = mergeRequest ?? gitlabIssue;
  return (
    <li style={{ borderLeft: `3px solid ${edge(item.score)}`, borderTop: rank > 1 && !(inSubject && !opens) ? '1px solid var(--color-border)' : undefined }}>
      {opens && (
        <div className="px-4 pt-3 pl-[3.25rem] text-xs">
          <p style={{ color: 'var(--color-text-3)' }}>
            {item.subject!.size} decisions about <span style={{ color: 'var(--color-text-2)' }}>{opens}</span>
          </p>
          {item.subject!.summary && <p className="mt-0.5 max-w-prose" style={{ color: 'var(--color-text-2)' }}>{item.subject!.summary}</p>}
          {!!item.subject!.sessions?.length && (
            <p className="mt-0.5 flex flex-wrap gap-x-2" style={{ color: 'var(--color-text-3)' }}>
              {item.subject!.sessions.length === 1 ? 'Session on it:' : 'Sessions on it:'}
              {item.subject!.sessions.map((s) => (
                <Link key={s.id} to={`/servers/${serverId}/sessions/${s.id}`} className="hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
                  {s.name}
                </Link>
              ))}
            </p>
          )}
        </div>
      )}
      <div className={`flex flex-wrap items-baseline gap-x-4 px-4 py-3 sm:flex-nowrap${inSubject ? ' sm:pl-8' : ''}`}>
        <Link to={item.url}
          onClick={() => {
            const anchor = anchorOf(item);
            if (anchor) setTimeout(() => document.getElementById(anchor)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 80);
          }}
          className="flex min-w-0 flex-1 basis-full items-baseline gap-4 focus-visible:outline focus-visible:outline-2 sm:basis-0">
          <span className="w-5 flex-shrink-0 text-right text-sm tabular-nums" style={{ color: 'var(--color-text-3)' }} aria-hidden="true">{rank}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium" style={{ color: 'var(--color-text)' }}>{ACTION[item.kind](item.title)}</span>
            <span className="mt-0.5 block text-xs" style={{ color: 'var(--color-text-2)' }}>{sentence(item.reasons)}</span>
          </span>
        </Link>
        <span className="ml-9 flex max-w-[calc(100%-2.25rem)] flex-shrink-0 flex-wrap items-baseline gap-x-3 sm:ml-0 sm:max-w-none sm:flex-nowrap">
          {item.server && <span className="text-xs" style={{ color: 'var(--color-text-2)' }}>{item.server.name}</span>}
          <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{timeAgo(item.since)}</span>
          {target && !prepare.isSuccess && (
            <button type="button" disabled={prepare.isPending} onClick={() => prepare.mutate(target)} className={quietButton}
              style={{ color: 'var(--color-brand-light)' }}>
              {prepare.isPending ? 'Asking…' : gitlabIssue ? 'Prepare the plan' : 'Prepare the review'}
            </button>
          )}
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className={quietButton}
            style={{ color: open ? 'var(--color-text)' : 'var(--color-text-2)' }}>
            Set aside
          </button>
        </span>
      </div>
      {target && (prepare.isSuccess || prepare.isError) && (
        <p role={prepare.isError ? 'alert' : 'status'} className="px-4 pb-3 pl-[3.25rem] text-xs"
          style={{ color: prepare.isError ? 'var(--color-error)' : 'var(--color-text-3)' }}>
          {prepare.isError ? `Not started: ${(prepare.error as Error).message}` : `${gitlabIssue ? 'Plan' : 'Review'} asked: it replaces this line when ready.`}
        </p>
      )}
      {open && (
        <div className="space-y-3 px-4 pb-3 pl-[3.25rem]" role="group" aria-label={`Set aside: ${ACTION[item.kind](item.title)}`}>
          <SetAsideForm serverId={serverId} itemId={item.id} onDone={() => setOpen(false)} onCancel={() => setOpen(false)} />
          <p className="flex flex-wrap items-baseline gap-x-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
            Handled elsewhere?
            <button type="button" disabled={setAside.isPending} className={quietButton}
              onClick={() => setAside.mutate({ itemId: item.id, action: 'dismiss' }, { onSuccess: () => setOpen(false) })}
              style={{ color: 'var(--color-text)', border: '1px solid var(--color-border-2)' }}>
              Already done
            </button>
            it comes back if it changes
          </p>
          {setAside.isError && <span role="alert" className="block text-xs" style={{ color: 'var(--color-error)' }}>Not marked done: {(setAside.error as Error).message}</span>}
          <SubjectFix item={item} serverId={serverId} subjects={subjects} onDone={() => setOpen(false)} />
        </div>
      )}
    </li>
  );
}

function HiddenRow({ item, serverId }: { item: HiddenInboxItem; serverId: string }) {
  const setAside = useSetAside(item.server?.id ?? serverId);
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 pl-3" style={{ borderLeft: '3px solid var(--color-border)' }}>
      <span className="min-w-0 flex-1">
        <Link to={item.url} className="block text-sm hover:underline" style={{ color: 'var(--color-text-2)' }}>{ACTION[item.kind](item.title)}</Link>
        <span className="block text-xs" style={{ color: 'var(--color-text-3)' }}>{item.server ? `${item.server.name}: ` : ''}{hiddenLine(item.hidden)}</span>
      </span>
      <button type="button" disabled={setAside.isPending} onClick={() => setAside.mutate({ itemId: item.id, action: 'bring-back' })}
        className={quietButton} style={{ color: 'var(--color-brand-light)' }}>
        Bring back
      </button>
    </li>
  );
}

function SetAside({ items, serverId }: { items: HiddenInboxItem[]; serverId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="text-xs hover:underline focus-visible:outline focus-visible:outline-2"
        style={{ color: 'var(--color-text-3)' }}>
        {items.length} set aside{open ? '' : ': show'}
      </button>
      {open && (
        <ul className="mt-2 space-y-2">
          {items.map((item) => <HiddenRow key={`${item.server?.id ?? ''}:${item.id}`} item={item} serverId={serverId} />)}
        </ul>
      )}
    </div>
  );
}

const JIRA_KINDS: ReadonlyArray<InboxItem['kind']> = ['jira', 'request'];
const BACKLOG = '';

/** The sprints the Jira decisions are in, the started one first; none when no board has sprints */
export function sprintsOf(items: InboxItem[]): Array<{ name: string; coming: boolean }> {
  const seen = new Map<string, boolean>();
  for (const i of items) if (i.sprint && !seen.has(i.sprint.name)) seen.set(i.sprint.name, !!i.sprint.coming);
  return [...seen].map(([name, coming]) => ({ name, coming })).sort((a, b) => Number(a.coming) - Number(b.coming) || a.name.localeCompare(b.name));
}

/** One sprint's Jira decisions, or the backlog's (`BACKLOG`); everything when `sprint` is null */
function inSprint(items: InboxItem[], sprint: string | null): InboxItem[] {
  if (sprint === null) return items;
  return items.filter((i) => JIRA_KINDS.includes(i.kind) && (sprint === BACKLOG ? !i.sprint : i.sprint?.name === sprint));
}

const chip = 'rounded px-1.5 py-0.5 text-xs focus-visible:outline focus-visible:outline-2';
const chipOn = { color: 'var(--color-text)', border: '1px solid var(--color-brand)' };
const chipOff = { color: 'var(--color-text-2)', border: '1px solid var(--color-border-2)' };

/** Decisions shown before « Show more »: the ranking is complete, only folded */
export const INBOX_FIRST = 3;

/** The servers a list spans, each once, in the order of their first decision */
function serversOf(items: InboxItem[]): Array<{ id: string; name: string }> {
  return [...new Map(items.filter((i) => i.server).map((i) => [i.server!.id, i.server!])).values()];
}

/**
 * The ranked decisions with their subjects, sprint and server filters, folded after the first ones, and those set
 * aside below: a server's « Waiting on you », or To do across servers when the items say their server
 */
export function DecisionList({ data, hidden, serverId = '', title, first = INBOX_FIRST, page = false, unread = [], filter }: {
  data: InboxItem[]; hidden: HiddenInboxItem[]; serverId?: string; title: string; first?: number; page?: boolean;
  /** Servers whose decisions could not be read: said, not left out silently */
  unread?: string[];
  /** To do's server and kind, kept in its address so the menu's shortcuts open it on them (operator, 30/10/2026) */
  filter?: { server: string | null; waiting: WaitingKind | null; onChange: (f: { server: string | null; waiting: WaitingKind | null }) => void };
}) {
  const [all, setAll] = useState(false);
  // On a board with sprints, the Jira decisions of one sprint or of the backlog (ADR-039 step 1b)
  const [sprint, setSprint] = useState<string | null>(null);
  // Across servers, the decisions of one of them (ADR-039 section 6)
  const [ownServer, setOwnServer] = useState<string | null>(null);
  const server = filter ? filter.server : ownServer;
  const waiting = filter?.waiting ?? null;
  const setServer = (id: string | null) => (filter ? filter.onChange({ server: id, waiting }) : setOwnServer(id));
  const servers = serversOf([...data, ...hidden]);
  const chosenServer = server !== null && servers.some((s) => s.id === server) ? server : null;
  const ofServer = <T extends InboxItem>(list: T[]) => {
    const on = chosenServer === null ? list : list.filter((i) => i.server?.id === chosenServer);
    return waiting === null ? on : on.filter((i) => waitingOf(i) === waiting);
  };
  // The kinds the list holds, for the filter by kind
  const kinds = filter ? WAITING.filter((w) => w.key === waiting || (chosenServer === null ? data : data.filter((i) => i.server?.id === chosenServer)).some((i) => waitingOf(i) === w.key)) : [];
  // Sprints are a board's: across servers two may share a name, so the filter comes once one server is shown
  const sprints = servers.length > 1 && chosenServer === null ? [] : sprintsOf(ofServer(data));
  const chosen = sprint !== null && sprint !== BACKLOG && !sprints.some((s) => s.name === sprint) ? null : sprint;
  const items = bySubject(inSprint(ofServer(data), sprints.length ? chosen : null));
  const aside = inSprint(ofServer(hidden), sprints.length ? chosen : null) as HiddenInboxItem[];
  const where = [
    waiting === null ? '' : `in ${WAITING.find((w) => w.key === waiting)?.label ?? waiting}`,
    chosenServer === null ? '' : `on ${servers.find((s) => s.id === chosenServer)?.name ?? chosenServer}`,
    !sprints.length || chosen === null ? '' : chosen === BACKLOG ? 'in the backlog' : `in ${chosen}`,
  ].filter(Boolean).join(' ');
  // One more than the first ones is not worth a « Show 1 more »; a subject is not cut by the fold
  let fold = first;
  while (fold < items.length && items[fold].subject && items[fold].subject!.size > 1 && items[fold].subject!.id === items[fold - 1].subject?.id) fold += 1;
  const shown = all || items.length <= fold + 1 ? items : items.slice(0, fold);
  const rest = items.length - shown.length;
  // The subjects of the list, each once, for « Same subject as »
  const subjects = [...new Map(items.filter((i) => i.subject).map((i) => [`${i.server?.id ?? serverId}:${i.subject!.id}`, {
    id: i.subject!.id, name: i.subject!.name, serverId: i.server?.id ?? serverId,
  }])).values()];
  const Heading = page ? 'h1' : 'h2';
  return (
    <section aria-labelledby="inbox-title" className="mb-8 min-w-0">
      <Heading id="inbox-title" className={page ? 'page-title text-2xl' : 'text-lg font-semibold'} style={{ color: 'var(--color-text)' }}>{title}</Heading>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>
        {items.length === 0
          ? `Nothing ${where ? `${where} ` : ''}waits on you${unread.length ? ' on the servers read' : ''}.`
          : `${items.length === 1 ? 'One decision' : `${items.length} decisions`}${where ? ` ${where}` : ''}, in the order to take them.`}
      </p>
      {unread.length > 0 && (
        <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-warn)' }}>
          Not read, try again in a minute: {unread.join(', ')}.
        </p>
      )}
      {servers.length > 1 && (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1.5" role="group" aria-label="Decisions of a server">
          <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>Server</span>
          {[{ key: null, label: 'Everything' }, ...servers.map((s) => ({ key: s.id, label: s.name }))].map((o) => (
            <button key={o.key ?? 'all'} type="button" aria-pressed={chosenServer === o.key} onClick={() => { setServer(o.key); setAll(false); }}
              className={chip} style={chosenServer === o.key ? chipOn : chipOff}>
              {o.label}
            </button>
          ))}
        </div>
      )}
      {filter && (kinds.length > 1 || waiting !== null) && (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1.5" role="group" aria-label="Decisions of a kind">
          <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>Kind</span>
          {[{ key: null, label: 'Everything' }, ...kinds.map((w) => ({ key: w.key, label: w.label }))].map((o) => (
            <button key={o.key ?? 'all'} type="button" aria-pressed={waiting === o.key} onClick={() => { filter.onChange({ server: chosenServer, waiting: o.key }); setAll(false); }}
              className={chip} style={waiting === o.key ? chipOn : chipOff}>
              {o.label}
            </button>
          ))}
        </div>
      )}
      {sprints.length > 0 && (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1.5" role="group" aria-label="Jira tickets of a sprint">
          <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>Sprint</span>
          {[{ key: null, label: 'Everything' }, ...sprints.map((s) => ({ key: s.name, label: s.coming ? `${s.name} (next)` : s.name })), { key: BACKLOG, label: 'Backlog' }].map((o) => (
            <button key={o.key ?? 'all'} type="button" aria-pressed={chosen === o.key} onClick={() => { setSprint(o.key); setAll(false); }}
              className={chip} style={chosen === o.key ? chipOn : chipOff}>
              {o.label}
            </button>
          ))}
        </div>
      )}
      {items.length > 0 && (
        <ol className="card mt-4 overflow-hidden">
          {shown.map((item, i) => {
            const inSubject = !!item.subject && item.subject.size > 1;
            const opens = inSubject && shown[i - 1]?.subject?.id !== item.subject!.id ? item.subject!.name : undefined;
            return <Row key={`${item.server?.id ?? ''}:${item.id}`} item={item} rank={i + 1} serverId={serverId} opens={opens} inSubject={inSubject} subjects={subjects} />;
          })}
        </ol>
      )}
      {(rest > 0 || (all && items.length > first + 1)) && (
        <button type="button" onClick={() => setAll(!all)} aria-expanded={all}
          className="mt-2 py-1 text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
          {all ? (first === INBOX_FIRST ? 'Show the first three only' : `Show the first ${first} only`) : `Show ${rest} more ${rest === 1 ? 'decision' : 'decisions'}`}
        </button>
      )}
      {aside.length > 0 && <SetAside items={aside} serverId={serverId} />}
    </section>
  );
}

export function Inbox({ serverId }: { serverId: string }) {
  const { data, error } = useInbox(serverId);
  const { data: hidden } = useHiddenInbox(serverId);
  if (error || (!(data ?? []).length && !(hidden ?? []).length)) return null;
  return <DecisionList data={data ?? []} hidden={hidden ?? []} serverId={serverId} title="Waiting on you" />;
}
